import { supabase } from './supabase'
import { captureException } from './errorTracking'
import { secureRPCWithSchema } from './sqlInjectionProtection'

/**
 * Breach Detection Service
 * Monitors and detects potential security breaches
 */

/**
 * Detect and log a breach event
 */
export const detectBreachEvent = async (
  organizationId,
  eventType,
  severity,
  description,
  affectedResources = [],
  affectedUsers = [],
  ipAddress = null
) => {
  try {
    const data = await secureRPCWithSchema('detect_breach_event', {
      org_id: organizationId,
      event_type_param: eventType,
      severity_param: severity,
      description_param: description,
      affected_resources_param: affectedResources,
      affected_users_param: affectedUsers,
      ip_address_param: ipAddress,
    })

    // If critical or high severity, trigger immediate notification
    if (severity === 'critical' || severity === 'high') {
      await sendBreachNotification(organizationId, data, severity)
    }

    return data
  } catch (error) {
    console.error('Error detecting breach event:', error)
    captureException(error, { context: 'breach_detection' })
    throw error
  }
}

/**
 * Send breach notification
 */
export const sendBreachNotification = async (organizationId, breachEventId, severity) => {
  try {
    // Get organization admins
    const { data: admins } = await supabase
      .from('organization_members')
      .select('user_id, users(email)')
      .eq('organization_id', organizationId)
      .eq('role', 'admin')

    if (!admins || admins.length === 0) return

    // Create notifications for each admin
    const notifications = admins.map((admin) => ({
      organization_id: organizationId,
      breach_event_id: breachEventId,
      notification_type: 'internal',
      recipient_type: 'admin',
      delivery_method: 'email',
      delivery_status: 'pending',
      notification_content: {
        severity,
        message: 'A security breach event has been detected. Please review immediately.',
      },
    }))

    const { error } = await supabase
      .from('breach_notifications')
      .insert(notifications)

    if (error) throw error

    // Send emails (would integrate with email service)
    // await sendBreachEmails(admins, breachEventId)

    return notifications
  } catch (error) {
    console.error('Error sending breach notification:', error)
    captureException(error, { context: 'breach_notification' })
    throw error
  }
}

/**
 * Get breach events
 */
export const getBreachEvents = async (organizationId, filters = {}) => {
  try {
    let query = supabase
      .from('breach_events')
      .select('*')
      .eq('organization_id', organizationId)
      .order('detected_at', { ascending: false })

    if (filters.status) {
      query = query.eq('status', filters.status)
    }

    if (filters.severity) {
      query = query.eq('severity', filters.severity)
    }

    if (filters.startDate) {
      query = query.gte('detected_at', filters.startDate)
    }

    if (filters.endDate) {
      query = query.lte('detected_at', filters.endDate)
    }

    if (filters.limit) {
      query = query.limit(filters.limit)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching breach events:', error)
    captureException(error, { context: 'get_breach_events' })
    throw error
  }
}

/**
 * Resolve breach event
 */
export const resolveBreachEvent = async (breachEventId, resolutionNotes) => {
  try {
    const { data, error } = await supabase
      .from('breach_events')
      .update({
        status: 'resolved',
        resolved_at: new Date().toISOString(),
        resolution_notes: resolutionNotes,
        resolved_by: (await supabase.auth.getUser()).data.user?.id,
      })
      .eq('id', breachEventId)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error resolving breach event:', error)
    captureException(error, { context: 'resolve_breach' })
    throw error
  }
}

/**
 * Get breach statistics
 */
export const getBreachStatistics = async (organizationId) => {
  try {
    const { data: events, error } = await supabase
      .from('breach_events')
      .select('severity, status, detected_at')
      .eq('organization_id', organizationId)

    if (error) throw error

    const stats = {
      total: events.length,
      bySeverity: {
        critical: events.filter((e) => e.severity === 'critical').length,
        high: events.filter((e) => e.severity === 'high').length,
        medium: events.filter((e) => e.severity === 'medium').length,
        low: events.filter((e) => e.severity === 'low').length,
      },
      byStatus: {
        detected: events.filter((e) => e.status === 'detected').length,
        investigating: events.filter((e) => e.status === 'investigating').length,
        resolved: events.filter((e) => e.status === 'resolved').length,
        false_positive: events.filter((e) => e.status === 'false_positive').length,
      },
      unresolved: events.filter((e) => e.status !== 'resolved' && e.status !== 'false_positive')
        .length,
      last30Days: events.filter(
        (e) => new Date(e.detected_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      ).length,
    }

    return stats
  } catch (error) {
    console.error('Error fetching breach statistics:', error)
    captureException(error, { context: 'get_breach_statistics' })
    throw error
  }
}

